import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {
    Alert, Box, Button, Chip, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {AltRoute} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import BetaBadge from "./layout/BetaBadge";
import NoDataLabel from "./layout/NoDataLabel";
import TableHeader from "./layout/table/TableHeader";
import Constants from "../../utils/constants";
import {useGetSimulationQuery} from "../../modules/simulations/simulations.api";
import BlocJournee from "./simulations/BlocJournee";
import VerdictSimulation from "./simulations/VerdictSimulation";
import BoutonExporter from "./simulations/BoutonExporter";
import BadgeImporte from "./simulations/BadgeImporte";
import {texteSnapshot} from "./simulations/echangeScenarioRules";
import {lignesNonAffectees, paramsUrl, verdictSimulation} from "./simulations/atelierRules";
import {
    formatHeuresDepuisMinutes, formatKm, libelleEquipage, paramFiger,
    pollingSimulation, raisonsGenerales, resumeContraint,
} from "./simulations/simulationRules";

const HEAD_JOURNEES = [
    {id: 'date', label: 'Date'},
    {id: 'equipe', label: 'Équipe'},
    {id: 'equipage', label: 'Équipage'},
    {id: 'trajet', label: 'Trajet'},
    {id: 'bacs', label: 'Bacs'},
    {id: 'heures', label: 'Heures'},
    {id: 'km', label: 'Km'},
    {id: 'bph', label: 'Bac/h'},
    {id: 'signal', label: 'Signal'},
];

/** Pastille de l'indicateur bac/h (vert / rouge, tokens du toggle Oui/Non). */
function Pastille({indicateur}) {
    const vert = indicateur === 'vert';
    return <span aria-label={vert ? 'vert' : 'rouge'}
                 style={{display: 'inline-block', width: 10, height: 10, borderRadius: '50%', marginRight: 6,
                     backgroundColor: vert ? 'seagreen' : 'firebrick'}}/>;
}

function SimulationPage() {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const clientId = searchParams.get('client');
    const navigate = useNavigate();
    const [polling, setPolling] = useState(0);
    const {data: simulation, isLoading, error} = useGetSimulationQuery(id, {pollingInterval: polling});
    const [date, setDate] = useState(null);

    useEffect(() => {
        setPolling(pollingSimulation(simulation));
    }, [simulation]);

    if (isLoading) {
        return (
            <>
                <PageTitle title="Scénario" icon={<AltRoute/>}/>
                <Skeleton variant="rectangular" height={120}/>
                <Skeleton variant="rectangular" height={300} sx={{mt: 2}}/>
            </>
        );
    }
    if (error || !simulation) {
        return (
            <>
                <PageTitle title="Scénario" icon={<AltRoute/>}/>
                <Alert severity="error">{error?.status === 404 ? "Scénario introuvable." : "Impossible de charger le scénario."}</Alert>
                <Button color="primary" onClick={() => navigate(Constants.APP_ROUTE_SIMULATIONS)}>Retour aux simulations</Button>
            </>
        );
    }

    const p = simulation.parametres || {};
    const t = simulation.totaux;
    const retour = Constants.APP_ROUTE_SIMULATIONS + paramsUrl({client: clientId, patrimoine: simulation.patrimoine?.id});
    const atelier = (extra = {}) => Constants.APP_ROUTE_SIMULATIONS_ATELIER + paramsUrl({client: clientId, patrimoine: simulation.patrimoine?.id, from: simulation.id, ...extra});
    const nonAffectees = simulation.nonAffectees?.liste ?? [];

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center">
                    <PageTitle title={simulation.libelle} icon={<AltRoute/>}/>
                    <BetaBadge/>
                    <BadgeImporte simulation={simulation} sx={{ml: 1}}/>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <BoutonExporter simulation={simulation}/>
                    <Button color="secondary" variant="contained" sx={{color: 'white', borderRadius: 999}} onClick={() => navigate(atelier())}>
                        Ouvrir dans l'atelier
                    </Button>
                    <Button color="primary" onClick={() => navigate(retour)}>Retour aux scénarios</Button>
                </Stack>
            </Stack>

            <Box sx={{mb: 2}}><VerdictSimulation verdict={verdictSimulation(simulation)}
                sousLigne={`${simulation.enregistre ? 'Enregistré' : 'Brouillon'}${simulation.statut === 'done' && t?.horizon != null ? ` · horizon ${t.horizon} journées` : ''}${texteSnapshot(simulation) ? ` · ${texteSnapshot(simulation)}` : ''}`}
                details={simulation.statut === 'done' ? lignesNonAffectees(simulation) : []}/></Box>

            <div className="sectionContainer">
                <Typography variant="body2">
                    {simulation.patrimoine?.libelle} · du {simulation.du} au {simulation.au}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mt: 1}}>
                    <Chip size="small" label={`${p.nbEquipes} équipe(s)`}/>
                    <Chip size="small" label={`équipage ${libelleEquipage(p.equipage)}`}/>
                    <Chip size="small" label={resumeContraint(p)}/>
                    <Chip size="small" label={p.deplacement === false ? 'aller-retour base' : 'déplacement'}/>
                    {p.curseurs && <Chip size="small" label={`${p.curseurs.heuresParJour} h × ${p.curseurs.joursParSemaine} j`}/>}
                    {p.curseurs?.coutKm != null && <Chip size="small" label={`${p.curseurs.coutKm} €/km`}/>}
                    {(p.verrous?.jours?.length > 0) && <Chip size="small" label={`${p.verrous.jours.length} PAV figé(s)`}/>}
                    {(p.verrous?.interdits?.length > 0) && <Chip size="small" label={`${p.verrous.interdits.length} date(s) interdite(s)`}/>}
                </Stack>

                {simulation.statut === 'done' && t &&
                    <>
                        {raisonsGenerales(simulation.raisons).map((r, i) => <Typography key={i} variant="caption" sx={{display: 'block'}}>{r}</Typography>)}
                    </>}
            </div>

            {simulation.statut === 'done' &&
                <>
                    <Typography variant="h5" className="sectionHeader" sx={{mt: 3}}>Journées</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small" aria-label="journées du scénario">
                            <TableHeader headCells={HEAD_JOURNEES} noAction/>
                            <TableBody>
                                {simulation.journees.length === 0 &&
                                    <TableRow><TableCell colSpan={HEAD_JOURNEES.length} align="center"><NoDataLabel label="Aucune journée dans ce scénario."/></TableCell></TableRow>}
                                {simulation.journees.map((j) => (
                                    <TableRow key={`${j.date}-${j.equipe}`} hover selected={date === j.date}
                                              style={{cursor: 'pointer'}} onClick={() => setDate(date === j.date ? null : j.date)}>
                                        <TableCell>{j.date}</TableCell>
                                        <TableCell>{j.equipe}</TableCell>
                                        <TableCell>
                                            {libelleEquipage(j.equipage)}
                                            {j.raisonEquipage && <Typography variant="caption" sx={{display: 'block', color: 'var(--thm-gray)'}}>{j.raisonEquipage}</Typography>}
                                        </TableCell>
                                        <TableCell>{j.libelleTrajet ?? '—'}</TableCell>
                                        <TableCell>{j.bacs}</TableCell>
                                        <TableCell>{formatHeuresDepuisMinutes(j.minutesTravail)}</TableCell>
                                        <TableCell>{formatKm(j.km)}</TableCell>
                                        <TableCell><Pastille indicateur={j.indicateur}/>{j.bacsParHeure}</TableCell>
                                        <TableCell>{j.signal ?? ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {date && <BlocJournee simulation={simulation} date={date} onFiger={(nom, d) => navigate(atelier({figer: paramFiger(nom, d)}))}/>}

                    {nonAffectees.length > 0 &&
                        <div className="sectionContainer">
                            <Typography variant="h5" className="sectionHeader">Visites non affectées ({simulation.nonAffectees.nombre})</Typography>
                            {nonAffectees.map((n, i) => (
                                <Typography key={i} variant="body2">
                                    <strong>{n.nomenclature}</strong> ({[].concat(n.flux ?? []).join(', ')}) — {n.raison}
                                </Typography>
                            ))}
                        </div>}
                </>}

            <Typography variant="caption" sx={{display: 'block', mt: 2}}>Simulation : ne crée rien dans l'ERP.</Typography>
        </>
    );
}

export default SimulationPage;
